/**
 * Extension loader — core primitives for dynamically loading extension UI
 * modules. Mirrors `plugin-loader` (Blob URL dynamic import) but detects an
 * `ExtensionUI`-shaped export (`manifest` + `activate`) instead of `PluginUI`.
 */
import type { ExtensionUI } from "@magic-md/extension-sdk";
import { registerExtensionUI } from "./extension-ui-registry";

function isExtensionUI(obj: unknown): obj is ExtensionUI {
    if (obj == null || typeof obj !== "object") return false;
    const e = obj as Record<string, unknown>;
    if (typeof e.activate !== "function") return false;
    const manifest = e.manifest as Record<string, unknown> | undefined;
    return (
        manifest != null &&
        typeof manifest === "object" &&
        typeof manifest.id === "string" &&
        typeof manifest.name === "string"
    );
}

/** Detect an ExtensionUI export (default first, then named). */
export function findExtensionUI(mod: Record<string, unknown>): ExtensionUI | null {
    if (isExtensionUI(mod.default)) return mod.default as ExtensionUI;
    for (const val of Object.values(mod)) {
        if (isExtensionUI(val)) return val as ExtensionUI;
    }
    return null;
}

/**
 * Import an extension UI module from JavaScript source text via same-origin
 * Blob URL (works in Tauri WKWebView, Web, and VSCode webview).
 */
export async function importExtensionUIFromSource(
    source: string,
    extensionId: string,
): Promise<ExtensionUI> {
    const blob = new Blob([source], { type: "application/javascript" });
    const blobUrl = URL.createObjectURL(blob);
    try {
        const mod = (await import(/* @vite-ignore */ blobUrl)) as Record<string, unknown>;
        const ui = findExtensionUI(mod);
        if (!ui) {
            throw new Error(`No ExtensionUI export found in ${extensionId}/dist/ui.js`);
        }
        return ui;
    } finally {
        URL.revokeObjectURL(blobUrl);
    }
}

/**
 * Load extensions from Vite glob imports (DEV mode / code-split bundles).
 *
 * ```ts
 * const modules = import.meta.glob('../../../extensions/* /src/ui.ts');
 * await loadBundledExtensions(modules);
 * ```
 */
export async function loadBundledExtensions(
    modules: Record<string, () => Promise<unknown>>,
): Promise<{ loaded: string[]; failed: string[] }> {
    const loaded: string[] = [];
    const failed: string[] = [];

    const entries = Object.entries(modules);
    const results = await Promise.allSettled(
        entries.map(async ([path, importFn]) => {
            const raw = await importFn();
            const mod = (raw != null && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
            const ui = findExtensionUI(mod);
            if (!ui) throw new Error(`No ExtensionUI export found in ${path}`);
            registerExtensionUI(ui);
            return ui.manifest.id;
        }),
    );

    for (const [i, result] of results.entries()) {
        if (result.status === "fulfilled") loaded.push(result.value);
        else failed.push(entries[i][0]);
    }

    return { loaded, failed };
}
